/**
 * Where the CI fleet screen gets its fleet-api access token. org-admin#96.
 *
 * ── THE ONE TOKEN THIS APP HOLDS, AND WHY IT IS SAFE TO ───────────────
 *
 * Everything else in the portal rides the HttpOnly session cookie and never
 * sees a credential (see the README). fleet-api refuses cookies: it verifies a
 * bearer token whose audience is its own, `<issuer>/app/fleet-api`. So this
 * screen needs a token, and how it holds one was decided on #96 (Jon, 13 Sep,
 * "Option A"):
 *
 *   - Authorization code with PKCE, as the public client `org-admin`.
 *   - The access token lives in THIS MODULE'S MEMORY. Never localStorage, and
 *     no refresh token is requested.
 *   - It is renewed on demand, only when a fleet-api request finds it expired
 *     (15 minutes), by a top-level redirect with `prompt=none` through the
 *     admin's existing auth session.
 *
 * Renewal needs the session cookie, which no script can read. So a token
 * lifted out of this page cannot renew itself and is dead within 15 minutes,
 * and fleet access ends within 15 minutes of the session ending.
 *
 * ── REJECTED, SO NOBODY REBUILDS THEM ────────────────────────────────
 *
 * A refresh token matched to the session length: a JS-held refresh token can be
 * exfiltrated, and fleet-api verifies offline, so signing out or revoking the
 * session would not stop it. Seven days of irrevocable access, on an API that
 * gains write actions with fleet-api#21 and #24.
 *
 * A keep-alive to fleet-api: whoever holds a stolen token can send the
 * keep-alive too, fleet-api cannot see the auth session, and it would make
 * fleet-api stateful.
 *
 * ── THE COST OF A REDIRECT ───────────────────────────────────────────
 *
 * Renewal reloads the portal. `usePoll` in CiFleet.tsx pauses while the tab is
 * hidden and after 30 idle minutes, so an open dashboard nobody is looking at
 * does not keep doing it. What the redirect must carry across is kept in
 * `sessionStorage` for that one round trip only: the PKCE verifier, the state,
 * and the hash to return to. Never the token.
 */
import { LOGIN_URL } from './auth-client.js';
import type { TokenSource } from './fleet-api.js';

/** Our registered client id in auth's `lib/clients.ts`, public (no secret). */
export const CLIENT_ID = 'org-admin';
export const CALLBACK_PATH = '/callback';

/** The audience fleet-api checks. Auth mints it for `org-admin` only because
 *  `CROSS_CLIENT_RESOURCE_GRANTS` declares the pair (auth#476). */
export const fleetResource = (loginUrl: string) => `${loginUrl}/app/fleet-api`;

/**
 * One service this portal holds a token for (fleet-api, support).
 *
 * The round trip below was written for fleet-api alone; support is the second
 * service with its own audience (RidgeWill/platform#29). Each gets its own
 * session, its own storage keys and its own wording, and they share the one
 * registered `/callback`: `completeCallbacks` hands each response to the
 * session whose saved `state` it carries.
 */
export interface ResourceSpec {
  /** The audience's slug, `<issuer>/app/<slug>`, as auth's clients.ts names it. */
  slug: string;
  /** What error messages call it. */
  label: string;
  /** The storage key segment: `rw-admin-<key>-pending`. */
  key: string;
  /** Where to land after the round trip when nothing better is known. */
  defaultReturn: string;
}

export const FLEET: ResourceSpec = { slug: 'fleet-api', label: 'fleet-api', key: 'fleet', defaultReturn: '#/ci-fleet' };
export const SUPPORT: ResourceSpec = { slug: 'support', label: 'support', key: 'support', defaultReturn: '#/support' };

/** Renew this long before the token's stated expiry, so a request in flight
 *  does not arrive a second after it lapsed and read as a 404 refusal. */
export const EXPIRY_SKEW_MS = 60_000;

/**
 * A second redirect this soon after the last is a loop, not a renewal.
 *
 * The same reasoning as `bounce.ts`: a round trip that fails to leave a token
 * behind would otherwise shuttle the tab between two origins forever.
 */
export const REDIRECT_GUARD_MS = 10_000;

const pendingKey = (spec: ResourceSpec) => `rw-admin-${spec.key}-pending`;
const redirectedAtKey = (spec: ResourceSpec) => `rw-admin-${spec.key}-redirected-at`;

interface Pending {
  verifier: string;
  state: string;
  returnTo: string;
}

interface Held {
  token: string;
  expiresAt: number;
}

// ── PKCE (RFC 7636) ──────────────────────────────────────────────────

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes, base64url: 43 characters, inside RFC 7636's 43 to 128. */
export function randomString(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)));
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export function authorizeUrl(p: {
  loginUrl: string;
  origin: string;
  state: string;
  challenge: string;
  /** The audience slug; fleet-api when omitted. */
  resource?: string;
}): string {
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: `${p.origin}${CALLBACK_PATH}`,
    scope: 'openid',
    // Never a sign-in form. No session means `login_required` comes straight
    // back, and the portal's own session gate is what sends somebody to sign in.
    prompt: 'none',
    state: p.state,
    code_challenge: p.challenge,
    code_challenge_method: 'S256',
    resource: `${p.loginUrl}/app/${p.resource ?? 'fleet-api'}`,
  });
  return `${p.loginUrl}/api/auth/oauth2/authorize?${query}`;
}

// ── THE SESSION ──────────────────────────────────────────────────────

export interface FleetSessionDeps {
  /** The hot-path auth origin: the issuer, and where authorize and token live.
   *  Not AUTH_URL, which is the admin deployment and serves neither. */
  loginUrl: string;
  now: () => number;
  store: () => Storage | undefined;
  location: () => Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>;
  /** Leaves the page. */
  navigate: (url: string) => void;
  /** Rewrites the address bar without leaving. */
  replaceUrl: (url: string) => void;
  fetch: typeof fetch;
  /**
   * Shared by every session on one page: set once any of them has left for
   * auth. One `/callback` can finish only one round trip, so a second session
   * starting its own in the same load would stamp its redirect guard, lose the
   * race to navigate, and on the way back read its own stamp as a round trip
   * that came back without a token. Omitted, a session has a lock of its own.
   */
  leaving?: { page: boolean };
}

export function createFleetSession(deps: FleetSessionDeps) {
  return createResourceSession(deps, FLEET);
}

export function createResourceSession(deps: FleetSessionDeps, spec: ResourceSpec) {
  const PENDING_KEY = pendingKey(spec);
  const REDIRECTED_AT_KEY = redirectedAtKey(spec);
  const resource = `${deps.loginUrl}/app/${spec.slug}`;
  let held: Held | null = null;
  /** Why the last round trip failed. Kept for the life of the page: redirecting
   *  again would ask the same question and get the same answer. */
  let failure: string | null = null;
  /** Shared by concurrent callers. The runners and queue panels ask at the same
   *  moment, and two redirects would race to overwrite one pending entry. */
  let renewing: Promise<string> | null = null;
  const leaving = deps.leaving ?? { page: false };

  const token: TokenSource = async () => {
    if (held && deps.now() < held.expiresAt - EXPIRY_SKEW_MS) return held.token;
    held = null;
    if (failure) throw new Error(failure);
    if (renewing) return renewing;

    const store = deps.store();
    if (!store) {
      throw new Error(`This browser is blocking session storage, which signing in to ${spec.label} needs.`);
    }
    const last = Number(safeGet(store, REDIRECTED_AT_KEY));
    if (Number.isFinite(last) && deps.now() - last < REDIRECT_GUARD_MS) {
      throw new Error(`Signing in to ${spec.label} came back without a token. Reload the page to try again.`);
    }
    // Another session is already taking the page to auth. Wait with it, and
    // ask again on the load that comes back.
    if (leaving.page) return new Promise<string>(() => {});
    leaving.page = true;

    renewing = (async () => {
      const verifier = randomString();
      const state = randomString();
      const here = deps.location();
      const pending: Pending = { verifier, state, returnTo: here.hash || spec.defaultReturn };
      try {
        store.setItem(PENDING_KEY, JSON.stringify(pending));
        store.setItem(REDIRECTED_AT_KEY, String(deps.now()));
      } catch {
        renewing = null;
        leaving.page = false;
        throw new Error(`This browser is blocking session storage, which signing in to ${spec.label} needs.`);
      }
      deps.navigate(
        authorizeUrl({
          loginUrl: deps.loginUrl,
          origin: here.origin,
          state,
          challenge: await challengeFor(verifier),
          resource: spec.slug,
        }),
      );
      // Never settles: the page is unloading, and a screen left in its loading
      // state for the moment that takes is the honest thing to show.
      return new Promise<string>(() => {});
    })();
    return renewing;
  };

  /**
   * Finish a round trip, BEFORE the app renders. Called from main.tsx.
   *
   * A no-op anywhere but `/callback`. It never throws: a failure is kept and
   * reported by the next `token()` call, on the screen that asked, rather than
   * as a white page.
   */
  async function completeCallback(): Promise<void> {
    const here = deps.location();
    if (here.pathname !== CALLBACK_PATH) return;

    const store = deps.store();
    const pending = store ? readPending(store, PENDING_KEY) : null;
    if (store) safeRemove(store, PENDING_KEY);

    try {
      const { code, verifier } = parseCallback(here.search, pending, deps.loginUrl, spec.label);
      const res = await deps.fetch(`${deps.loginUrl}/api/auth/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          redirect_uri: `${here.origin}${CALLBACK_PATH}`,
          client_id: CLIENT_ID,
          code_verifier: verifier,
          resource,
        }),
        // The code and verifier are the proof. The cookie adds nothing here.
        credentials: 'omit',
      });
      const body = (await res.json().catch(() => ({}))) as {
        access_token?: string;
        expires_in?: number;
        error?: string;
        error_description?: string;
      };
      if (!res.ok || !body.access_token) {
        throw new Error(
          `The auth service would not issue a ${spec.label} token: ${body.error_description || body.error || `HTTP ${res.status}`}.`,
        );
      }
      held = { token: body.access_token, expiresAt: deps.now() + (body.expires_in ?? 0) * 1000 };
      if (store) safeRemove(store, REDIRECTED_AT_KEY);
    } catch (e) {
      failure = e instanceof Error ? e.message : String(e);
    }
    // Back to where the renewal started, whatever happened, so the address bar
    // never keeps a spent code and a reload does not replay it.
    deps.replaceUrl(`/${pending?.returnTo ?? spec.defaultReturn}`);
  }

  /**
   * Whether the response on `/callback` is this session's: its saved `state`
   * matches the one in the URL. Read without consuming, so the owner's
   * `completeCallback` still finds it.
   */
  function ownsCallback(): boolean {
    const here = deps.location();
    if (here.pathname !== CALLBACK_PATH) return false;
    const store = deps.store();
    const pending = store ? readPending(store, PENDING_KEY) : null;
    const state = new URLSearchParams(here.search).get('state');
    return Boolean(pending && state && pending.state === state);
  }

  /**
   * The token already held, or null. Never renews, so never leaves the page.
   *
   * For the CI fleet alert count on the nav (org-admin#97), which runs on every
   * screen. `token()` there would send an admin reading Users through auth and
   * back, on every page load, to fetch a count they did not ask for. So the
   * count uses what the CI fleet screen already signed in for, and waits until
   * that screen has been opened.
   */
  const peek = (): string | null => (held && deps.now() < held.expiresAt - EXPIRY_SKEW_MS ? held.token : null);

  return { token, peek, completeCallback, ownsCallback };
}

export type ResourceSession = ReturnType<typeof createResourceSession>;

/**
 * Finish whichever round trip `/callback` belongs to, and only that one.
 *
 * Before there were two sessions, fleet's `completeCallback` ran on every
 * `/callback` and treated one it had not started as an attack ("did not ask
 * for"), recording a failure that stuck for the life of the page. With a
 * second service that would break fleet every time support signed in, so the
 * response goes to the session whose saved `state` it carries. One nobody
 * owns still goes to the first, which refuses it exactly as before.
 */
export async function completeCallbacks(sessions: readonly ResourceSession[]): Promise<void> {
  const owner = sessions.find((s) => s.ownsCallback()) ?? sessions[0];
  await owner?.completeCallback();
}

/** The code and the verifier that goes with it, or a thrown sentence saying why
 *  there are none. Exported for tests. */
export function parseCallback(
  search: string,
  pending: Pending | null,
  issuer: string,
  label = 'fleet-api',
): { code: string; verifier: string } {
  const q = new URLSearchParams(search);
  if (!pending || !q.get('state') || q.get('state') !== pending.state) {
    // Not ours: a stale tab, a second tab's round trip, or a forged link.
    throw new Error('A sign-in response arrived that this tab did not ask for. Reload the page to try again.');
  }
  // RFC 9207: a response naming another issuer is a mix-up, whatever it carries.
  const iss = q.get('iss');
  if (iss && iss !== issuer) {
    throw new Error(`A sign-in response came from ${iss}, not ${issuer}. It was refused.`);
  }
  const error = q.get('error');
  if (error === 'login_required') {
    throw new Error(`The auth service reports no signed-in session, so ${label} cannot be reached. Sign out and sign in again.`);
  }
  if (error) {
    throw new Error(`Signing in to ${label} failed: ${q.get('error_description') || error}.`);
  }
  const code = q.get('code');
  if (!code) throw new Error('The sign-in response carried no code. Reload the page to try again.');
  return { code, verifier: pending.verifier };
}

function readPending(store: Storage, key: string): Pending | null {
  try {
    const raw = store.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as Partial<Pending>) : null;
    return parsed?.verifier && parsed.state && typeof parsed.returnTo === 'string' ? (parsed as Pending) : null;
  } catch {
    return null;
  }
}

function safeGet(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(store: Storage, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    // Nothing to do; a leftover stamp lapses after REDIRECT_GUARD_MS.
  }
}

const browserDeps: FleetSessionDeps = {
  loginUrl: LOGIN_URL,
  now: () => Date.now(),
  store: () => {
    try {
      return window.sessionStorage;
    } catch {
      return undefined;
    }
  },
  location: () => window.location,
  navigate: (url) => window.location.assign(url),
  replaceUrl: (url) => window.history.replaceState(null, '', url),
  fetch: (...args) => fetch(...args),
  leaving: { page: false },
};

export const fleetSession = createFleetSession(browserDeps);

/** The support API's session (RidgeWill/platform#29). See `ResourceSpec`. */
export const supportSession = createResourceSession(browserDeps, SUPPORT);

export const supportToken: TokenSource = supportSession.token;

export const fleetToken: TokenSource = fleetSession.token;

/** Thrown by `fleetTokenIfHeld` when this page has not signed in to fleet-api yet. */
export class NoFleetToken extends Error {
  constructor() {
    super('Not signed in to fleet-api on this page yet.');
    this.name = 'NoFleetToken';
  }
}

/** A token source that never redirects: the held token, or NoFleetToken. */
export const fleetTokenIfHeld: TokenSource = async () => {
  const held = fleetSession.peek();
  if (!held) throw new NoFleetToken();
  return held;
};
